import type { EventDataType } from '@ixigo/analytics';
import { globalTracker } from '@/utils/track';

// CleverTap (+ GA, via globalTracker's fan-out) events for the CBCC
// (travel-credit-card) flow. Ported from fintech-ui's `cbccEventTracker`; see
// the CBCC CleverTap Event Reference. Names are the EXACT CleverTap event
// strings — do not rename, the analytics contract keys on them.
export const CBCC_EVENT = {
	/** CVP landing page mounted (fires once per load). */
	CVP_PAGE_LOAD: 'CBCC CVP Page Load',
	/** CVP mount when a `source` query param is present (returned from elsewhere). */
	BACK_TO_IXIGO: 'CBCC Back to ixigo',
	/** User left the CVP page via a back press (in-page control or device back). */
	CVP_PAGE_BACKPRESS: 'CBCC CVP Page Backpress',
	/** Apply CTA clicked — fired before the login check (logged in or out). */
	APPLY_CTA: 'CBCC Apply CTA',
	/** Pincode eligibility API responded. */
	PINCODE_ELIGIBILITY: 'CBCC Pincode Eligibility',
	/** Personal-details (apply) form opened. */
	DETAILS_PAGE_LOAD: 'CBCC Personal Details Page Load',
	/** Apply form submitted (validation passed). */
	DETAILS_FORM_SUBMITTED: 'CBCC Personal Details Form Submitted',
	/** ONBOARDING flow handed off to the bank (redirect URL returned). */
	REDIRECTED_TO_BANK: 'CBCC User Redirected to Bank',
	/** APPROVED flow opened manage-card (redirect URL returned). */
	MANAGE_CARD: 'CBCC Manage Card',
} as const;

type CbccEventName = (typeof CBCC_EVENT)[keyof typeof CBCC_EVENT];

// CVP-derived properties sent on EVERY cbcc event (mirrors fintech-ui's
// getCommonData). They live in the route loader, not in the services/hooks that
// fire most events, so the CVP page pushes them here once (setCbccCommonProps)
// and trackCbccEvent merges them into every event. Module-level (not React
// state) so non-component call sites — the bank-redirect hooks, the pincode
// service — pick them up too. globalTracker.track already injects the app-wide
// commons (App ID / User ID / Device ID / Source), so these layer on top.
let cbccCommonProps: Partial<EventDataType> = {};

/**
 * Set the CVP-derived common props carried by every subsequent cbcc event.
 * Called from the CVP page once CVP resolves (and again after an in-page login
 * re-resolves it). Keys are the exact CleverTap contract keys.
 */
export function setCbccCommonProps(props: {
	status?: string;
	userEligibleToApply?: boolean;
	isUserLoggedIn: boolean;
}): void {
	cbccCommonProps = {
		'User Status': props.status ?? '',
		'Onboarding Eligibility':
			props.userEligibleToApply === undefined ? '' : props.userEligibleToApply,
		isUserLoggedIn: props.isUserLoggedIn,
	};
}

/**
 * Track a CBCC event with the app-wide commons (via globalTracker) + the
 * CVP-derived commons (cbccCommonProps) + any per-event data.
 *
 * Every event carries `UI Version: 'new'` so analytics can distinguish this
 * (fin-apps / travel-credit-card) implementation from the legacy fintech-ui
 * CBCC flow that fires the same event names. Set first so per-event data can't
 * accidentally override it.
 */
export function trackCbccEvent(
	eventName: CbccEventName,
	eventData?: Partial<EventDataType>,
): void {
	globalTracker.track(eventName, {
		'UI Version': 'new',
		...cbccCommonProps,
		...eventData,
	});
}
